import { Input } from 'components/atoms';
import {
  FormInput,
  InfoBox,
  LabeledStatus,
  TabsNavigation,
} from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { IEventResponse } from 'services/events/types';
import { STATUS } from 'services/types';
import { getApplicationSettings } from 'store/state';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import {
  getTabError,
  requiredEnumSelect,
} from 'utils/global/validation.utils';
import { object, string, date } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import EventLabels from './tabs/EventLabels';
import EventBalance from './tabs/EventBalance';

export enum EventTabs {
  labels,
  balance,
  income,
  costs,
  statistics,
}

export interface IEventForm {
  version: number;
  id: string;
  name: string;
  status: number;
  startDate: string;
  endDate: string;
  labels: Array<{
    labelId: string;
    divisionPercentage: number;
  }>;
}

const eventInitialValues: IEventForm = {
  version: 0,
  id: '',
  name: '',
  status: STATUS.draft,
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  labels: [],
};

const eventValidationSchema = object().shape({
  name: string().required(() => i18next.t('formik-error.required')),
  status: requiredEnumSelect(STATUS),
  startDate: string().required(() => i18next.t('formik-error.required')),
  endDate: string()
    .required(() => i18next.t('formik-error.required'))
    .test('end-date-after-start', i18next.t('formik-error.end-date-after-start'), function(value) {
      const { startDate } = this.parent;
      if (startDate && value) {
        return new Date(value) > new Date(startDate);
      }
      return true;
    }),
});

export interface IEventFormProps
  extends FormikConfig<IEventForm>,
    IFormProps,
    II18nextPrefix {}

const EventForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  disableTabs,
  backPath,
  customBackItem,
  initialValues = eventInitialValues,
  status,
  ...props
}: IEventFormProps) => {
  const { t } = useTranslation();
  const { getStatusOptions } = useEnumToSelectOptions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <Formik
      {...props}
      initialValues={initialValues}
      validationSchema={eventValidationSchema}
      initialStatus={status}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={true}>
      {({
        values,
        setFieldValue,
        status,
        errors,
      }: IFormikProps<IEventForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              title={status?.displayMode ? tittle : undefined}
              customBackItem={customBackItem}
              path={backPath}
              topbarContent={
                <div className=' w-full flex justify-end'>
                  {status?.displayMode ? null : (
                    <Input.ComplexWrapper sizes={[70, 30]} className='w-96'>
                      <FormInput.Text
                        name='name'
                        label={t('input.event.name')}
                        autoFocus
                      />
                    </Input.ComplexWrapper>
                  )}
                  <div className='flex items-center gap-2 w-full justify-end'>
                    {
                      (initialValues.id && initialValues.id !== '') && (
                        status?.displayMode ? (
                          <LabeledStatus status={initialValues.status} />
                        ) : (
                          <div className='w-48'>
                            <FormInput.Select
                              name='status'
                              label={t('input.event.status')}
                              options={getStatusOptions(
                                STATUS,
                                initialValues.status,
                                'enum.STATUS'
                              )}
                            />
                          </div>
                        )
                      )
                    }

                    <div>{topbarContent}</div>
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={3}>
                  <FormInput.Text
                    name='startDate'
                    label={t('input.event.startDate')}
                    type='date'
                  />
                  <FormInput.Text
                    name='endDate'
                    label={t('input.event.endDate')}
                    type='date'
                  />
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: EventTabs.labels,
                    name: 'labels',
                  },
                  {
                    value: EventTabs.balance,
                    name: 'balance',
                    permissionName: 'EventIncomePermissionsLIST**',
                  },
                ]}
                disabled={disableTabs}
                i18nextPrefix={i18nextPrefix}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

EventForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    {
      path: 'labels',
      element: <EventLabels />,
    },
    {
      path: 'balance',
      element: <EventBalance />,
    },
  ].filter(children => !disabledChildren?.includes(children.path));

export default EventForm;
